import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import { useTaskTab } from './TaskTabContext.js'; // Import the existing context

const TaskSelectionContext = createContext();

export const TaskSelectionProvider = ({ children }) => {
  const { taskTab, setTaskTab } = useTaskTab();
  //const [refreshKey, setRefreshKey] = useState(0);
  
  // Card click state - tracks which card was clicked
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Filters state
  const [filters, setFilters] = useState({
    taskName: "",
    courtRef: "",
    startDate: "",
    endDate: "",
    assignedBy: [],
    assignedTo: [],
    status:""
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalTasks: 0
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Store filtered tasks for each tab combination
  const [filteredTasksByTab, setFilteredTasksByTab] = useState({});
  
  const prevTabKeyRef = useRef('');
  const lastUpdateTimeRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerTaskRefresh = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1);
  }, []);

  
  const currentTabKey = useMemo(() => {
    const tabKey = `${taskTab}_${selectedCard || 'all'}`;
    if (prevTabKeyRef.current !== tabKey) {
      prevTabKeyRef.current = tabKey;
    }
    return tabKey;
  }, [taskTab, selectedCard]);
  
  const filteredTasks = useMemo(() => {
    return filteredTasksByTab[currentTabKey] || [];
  }, [filteredTasksByTab, currentTabKey]);
  
const setFilteredTasks = useCallback((tasks) => {
  const now = Date.now();
  
  // Throttling to prevent excessive updates
  if (now - lastUpdateTimeRef.current < 50) {
    return;
  }
  lastUpdateTimeRef.current = now;
  
  setFilteredTasksByTab(prev => {
    const currentTasks = prev[currentTabKey];
    
    // Always update if tasks is empty array (important for clearing)
    if (Array.isArray(tasks) && tasks.length === 0) {
      return {
        ...prev,
        [currentTabKey]: []
      };
    }
    
    // Check if tasks are actually different
    if (currentTasks === tasks) {
      return prev;
    }
    
    if (currentTasks && currentTasks.length === tasks.length) {
      let hasChanged = false;
      for (let i = 0; i < tasks.length; i++) {
        if (currentTasks[i] !== tasks[i]) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged) {
        return prev;
      }
    }
    
    return {
      ...prev,
      [currentTabKey]: Array.isArray(tasks) ? [...tasks] : []
    };
  });
  
  // Set loading to false after tasks are updated
  setIsLoading(false);
}, [currentTabKey]);
    
  const buildQueryParams = useCallback(() => {
  const params = new URLSearchParams();

  // ✅ Pagination - Only add if limit is not "all"
  if (pagination.limit !== "all") {
    params.set("page", pagination.page.toString());
    params.set("limit", pagination.limit.toString());
  }

  // ✅ Search term
  if (searchTerm?.trim()) {
    params.set("search", searchTerm.trim());
  }

  // ✅ Card-based filtering
  if (selectedCard) {
    switch (selectedCard) {
      case "completed":
        params.set("isCompleted", "true");
        params.set("onClick", "completed");
        break;
      case "pending":
        params.set("isCompleted", "false");
        params.set("onClick", "pending");
        break;
      case "today":
        params.set("onClick", "today");
        break;
      case "total":
        params.set("onClick", "total");
        break;
      default:
        break;
    }
  }

  // Object.entries(filters).forEach(([key, value]) => {
  //   if (Array.isArray(value) && value.length > 0) {
  //     params.set(key, value.join(",")); // e.g., assignedTo=1,2,3
  //   } else if (typeof value === "string" && value.trim()) {
  //     params.set(key, value.trim());
  //   }
  // });

// In TaskSelectionContext.js -> buildQueryParams

Object.entries(filters).forEach(([key, value]) => {
  if (Array.isArray(value) && value.length > 0) {
    params.set(key, value.join(","));
  } else if (typeof value === "string" && value.trim() && value.trim() !== "all") {
    if (key === "status") {
      params.set("isCompleted", value.trim()); 
    } else {
      // For all other filters, use the key as is.
      params.set(key, value.trim());
    }
  }
});

  return params.toString();
}, [pagination, searchTerm, selectedCard, filters]);

  
  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      hasMore: true
    }));
  }, []);
  
  // Clear all filters and reset state
const clearAllFilters = useCallback(() => {
  setIsLoading(true);
  
  setFilters({
    taskName: "",
    courtRef: "",
    startDate: "",
    endDate: "",
    assignedBy: [],
    assignedTo: [],
    status:""
  });
  
  setSearchTerm('');
  setSelectedCard(null);
  resetPagination();
  
  // Clear the filtered tasks for current tab
  setFilteredTasksByTab(prev => ({
    ...prev,
    [currentTabKey]: []
  }));
  
}, [resetPagination, currentTabKey]);
  
  // Handle card click
  // const handleCardClick = useCallback((cardType) => {
  //   setSelectedCard(prevCard => prevCard === cardType ? null : cardType);
  //   resetPagination();
  // }, [resetPagination]);

  const handleCardClick = useCallback((cardType) => {
    setIsLoading(true); // Start loading
  
    // If the "Total Cases" card is clicked, reset EVERYTHING.
    if (cardType === "" || cardType === "total" || cardType === null) {
      clearAllFilters(); 
    } else {
      // For any other card, just toggle its state as before.
      setSelectedCard(prevCard => prevCard === cardType ? null : cardType);
      resetPagination();
    }
  }, [clearAllFilters, resetPagination]);
  
  // Stable setters
  const setFiltersStable = useCallback((newFilters) => {
    setFilters(prevFilters => {
      const hasChanged = Object.keys(newFilters).some(key => 
        newFilters[key] !== prevFilters[key]
      );
      
      if (hasChanged) {
        resetPagination();
        return newFilters;
      }
      
      return prevFilters;
    });
  }, [resetPagination]);
  
  const setSearchTermStable = useCallback((term) => {
    if (term !== searchTerm) {
      setSearchTerm(term);
      resetPagination();
    }
  }, [searchTerm, resetPagination]);
  
  // Check if any filters are active
  // const hasActiveFilters = useMemo(() => {
  //   return selectedCard !== null ||
  //          searchTerm.trim() !== '' ||
  //          Object.values(filters).some(value => value && value.trim() !== '');
  // }, [selectedCard, searchTerm, filters]);

  const hasActiveFilters = useMemo(() => {
  return selectedCard !== null ||
         searchTerm.trim() !== '' ||
         Object.values(filters).some(value => {
           if (Array.isArray(value)) {
             return value.length > 0;
           }
           return value && value.trim() !== '';
         });
}, [selectedCard, searchTerm, filters]);

  // Update pagination info
  const updatePaginationInfo = useCallback((totalTasks, hasMore) => {
    setPagination(prev => ({
      ...prev,
      totalTasks,
      hasMore
    }));
  }, []);
  
  // Load more tasks (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  }, [pagination.hasMore]);
  
  const contextValue = useMemo(() => ({
    // Tab management (from existing context)
    taskTab,
    setTaskTab,
    
    // Card selection
    selectedCard,
    setSelectedCard,
    handleCardClick,
    
    // Filters
    filters,
    setFilters: setFiltersStable,
    
    // Search
    searchTerm,
    setSearchTerm: setSearchTermStable,
    
    // Pagination
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    
    // Filtered tasks
    filteredTasks,
    setFilteredTasks,
    
    // Loading state
    isLoading,
    setIsLoading,
    refreshKey,
    triggerTaskRefresh,
    
    // Utilities
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    currentTabKey,
  }), [
    taskTab,
    setTaskTab,
    selectedCard,
    handleCardClick,
    filters,
    setFiltersStable,
    searchTerm,
    setSearchTermStable,
    pagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    filteredTasks,
    setFilteredTasks,
    isLoading,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    currentTabKey,
    refreshKey,
    triggerTaskRefresh
  ]);
  
  return (
    <TaskSelectionContext.Provider value={contextValue}>
      {children}
    </TaskSelectionContext.Provider>
  );
};

export const useTaskSelection = () => {
  const context = useContext(TaskSelectionContext);
  if (!context) {
    throw new Error('useTaskSelection must be used within a TaskSelectionProvider');
  }
  return context;
};